/**
 * Renders scannable barcodes for At a glance rows.
 * Supports EAN-13, EAN-8, and ITF-14 (14-digit box / GTIN barcodes).
 */
(function () {
  const FILL = '#000000';
  const BG = '#ffffff';
  const MODULE = 2;
  const BAR_HEIGHT = 36;
  const PAD_TOP = 4;
  const TEXT_BAND = 16;
  const QUIET_LEFT = 7;
  const QUIET_RIGHT = 5;

  const L = {
    0: '0001101', 1: '0011001', 2: '0010011', 3: '0111101', 4: '0100011',
    5: '0110001', 6: '0101111', 7: '0111011', 8: '0110111', 9: '0001011',
  };
  const G = {
    0: '0100111', 1: '0110011', 2: '0011011', 3: '0100001', 4: '0011101',
    5: '0111001', 6: '0000101', 7: '0010001', 8: '0001001', 9: '0010111',
  };
  const R = {
    0: '1110010', 1: '1100110', 2: '1101100', 3: '1000010', 4: '1011100',
    5: '1001110', 6: '1010000', 7: '1000100', 8: '1001000', 9: '1110100',
  };
  const PARITY = {
    0: 'LLLLLL', 1: 'LLGLGG', 2: 'LLGGLG', 3: 'LLGGGL', 4: 'LGLLGG',
    5: 'LGGLLG', 6: 'LGGGLL', 7: 'LGLGLG', 8: 'LGLGGL', 9: 'LGGLGL',
  };
  const ITF = {
    0: 'nnWWn',
    1: 'WnnnW',
    2: 'nWnnW',
    3: 'WWnnn',
    4: 'nnWnW',
    5: 'WnWnn',
    6: 'nWWnn',
    7: 'nnnWW',
    8: 'WnnWn',
    9: 'nWnWn',
  };

  function digitsOnly(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function eanCheckDigit(value) {
    let sum = 0;
    for (let i = 0; i < value.length; i += 1) {
      const digit = Number(value[i]);
      const weight = (value.length - 1 - i) % 2 === 0 ? 3 : 1;
      sum += digit * weight;
    }
    return String((10 - (sum % 10)) % 10);
  }

  function hasValidCheckDigit(digits) {
    const body = digits.slice(0, -1);
    return eanCheckDigit(body) === digits.slice(-1);
  }

  function normalizeEan13(code) {
    let digits = digitsOnly(code);
    if (digits.length === 12) digits += eanCheckDigit(digits);
    return digits.length === 13 ? digits : null;
  }

  function normalizeEan8(code) {
    let digits = digitsOnly(code);
    if (digits.length === 7) digits += eanCheckDigit(digits);
    return digits.length === 8 ? digits : null;
  }

  function normalizeItf14(code) {
    const digits = digitsOnly(code);
    return digits.length === 14 ? digits : null;
  }

  function encodeEan13(digits) {
    const parity = PARITY[digits[0]];
    if (!parity) return null;

    let pattern = '101';
    for (let i = 0; i < 6; i += 1) {
      const digit = digits[i + 1];
      pattern += parity[i] === 'L' ? L[digit] : G[digit];
    }
    pattern += '01010';
    for (let i = 0; i < 6; i += 1) {
      pattern += R[digits[i + 7]];
    }
    pattern += '101';
    return pattern;
  }

  function encodeEan8(digits) {
    let pattern = '101';
    for (let i = 0; i < 4; i += 1) pattern += L[digits[i]];
    pattern += '01010';
    for (let i = 0; i < 4; i += 1) pattern += R[digits[i + 4]];
    return `${pattern}101`;
  }

  function encodeItf(digits, narrow, wide) {
    if (digits.length % 2 !== 0) return null;

    const bar = (isWide) => '1'.repeat(isWide ? wide : narrow);
    const gap = (isWide) => '0'.repeat(isWide ? wide : narrow);

    let pattern = bar(false) + gap(false) + bar(false) + gap(false);

    for (let i = 0; i < digits.length; i += 2) {
      const bars = ITF[digits[i]];
      const spaces = ITF[digits[i + 1]];
      if (!bars || !spaces) return null;
      for (let j = 0; j < 5; j += 1) {
        pattern += bar(bars[j] === 'W');
        pattern += gap(spaces[j] === 'W');
      }
    }

    pattern += bar(true) + gap(false) + bar(false);
    return pattern;
  }

  function buildPattern(code) {
    const itf14 = normalizeItf14(code);
    if (itf14) {
      return {
        pattern: encodeItf(itf14, 1, 2),
        text: itf14,
        kind: 'itf14',
        module: MODULE,
        quietLeft: QUIET_LEFT,
        quietRight: QUIET_RIGHT
      };
    }

    const ean13 = normalizeEan13(code);
    if (ean13) {
      return {
        pattern: encodeEan13(ean13),
        text: ean13,
        kind: 'ean13',
        module: MODULE,
        quietLeft: QUIET_LEFT,
        quietRight: QUIET_RIGHT,
      };
    }

    const ean8 = normalizeEan8(code);
    if (ean8) {
      return {
        pattern: encodeEan8(ean8),
        text: ean8,
        kind: 'ean8',
        module: MODULE,
        quietLeft: QUIET_LEFT,
        quietRight: QUIET_RIGHT,
      };
    }

    return null;
  }

  const SVG_NS = 'http://www.w3.org/2000/svg';

  function el(name, attrs) {
    const node = document.createElementNS(SVG_NS, name);
    Object.keys(attrs).forEach((key) => node.setAttribute(key, String(attrs[key])));
    return node;
  }

  const VIEW_W = 360;
  const VIEW_H = 96;
  const VIEW_PAD_TOP = 8;
  const VIEW_TEXT_BAND = 28;
  const VIEW_BAR_HEIGHT = VIEW_H - VIEW_PAD_TOP - VIEW_TEXT_BAND;

  function mountBarcodeSvg(container, encoded) {
    const quietLeft = encoded.quietLeft == null ? QUIET_LEFT : encoded.quietLeft;
    const quietRight = encoded.quietRight == null ? QUIET_RIGHT : encoded.quietRight;
    const modules = quietLeft + encoded.pattern.length + quietRight;
    const module = VIEW_W / modules;

    const svg = el('svg', {
      class: 'dimark-glance__barcode-svg',
      viewBox: `0 0 ${VIEW_W} ${VIEW_H}`,
      preserveAspectRatio: 'none',
      role: 'img',
      'aria-label': encoded.text
    });

    svg.appendChild(el('rect', { x: 0, y: 0, width: VIEW_W, height: VIEW_H, fill: BG }));

    const bars = el('g', { 'shape-rendering': 'crispEdges', fill: FILL });

    let run = 0;
    for (let i = 0; i <= encoded.pattern.length; i += 1) {
      if (encoded.pattern[i] === '1') {
        run += 1;
        continue;
      }
      if (run > 0) {
        bars.appendChild(el('rect', {
          x: (quietLeft + i - run) * module,
          y: VIEW_PAD_TOP,
          width: run * module,
          height: VIEW_BAR_HEIGHT
        }));
        run = 0;
      }
    }

    svg.appendChild(bars);

    svg.appendChild(el('text', {
      x: VIEW_W / 2,
      y: VIEW_PAD_TOP + VIEW_BAR_HEIGHT + 20,
      fill: FILL,
      'text-anchor': 'middle',
      'font-family': 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      'font-size': 18,
      'letter-spacing': 0.6
    })).textContent = encoded.text;

    container.replaceChildren(svg);
    container.hidden = false;
  }

  function renderBarcodeGraphic(container) {
    const code = container.getAttribute('data-dimark-barcode');
    if (!code) {
      container.replaceChildren();
      container.hidden = true;
      return;
    }

    const encoded = buildPattern(code);
    if (!encoded || !encoded.pattern) {
      container.replaceChildren();
      container.hidden = true;
      return;
    }

    if (hasValidCheckDigit(encoded.text)) {
      container.removeAttribute('data-dimark-barcode-invalid');
    } else {
      container.setAttribute('data-dimark-barcode-invalid', 'true');
    }

    mountBarcodeSvg(container, encoded);
  }

  function initBarcodes(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-dimark-barcode]').forEach(renderBarcodeGraphic);
  }

  window.dimarkInitBarcodes = initBarcodes;

  function scheduleInit(root) {
    window.requestAnimationFrame(() => initBarcodes(root || document));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleInit(document));
  } else {
    scheduleInit(document);
  }

  window.addEventListener('load', () => scheduleInit(document));
  document.addEventListener('shopify:section:load', (event) => scheduleInit(event.target));
  document.addEventListener('on:variant:change', () => {
    window.setTimeout(() => scheduleInit(document), 60);
  });
})();
